import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  AutoAttendantNodeData,
  AutoAttendantOption,
} from '../../../converters/shared/reactFlowTypes'
import { withSoundClipSelection } from './soundClipSelection'

/**
 * Hook for Auto Attendant node functionality.
 * Handles sound clip, timeout, repeat times, allow extension dialing, and options.
 */
export const useAutoAttendantNode = (
  id: string,
  data: AutoAttendantNodeData,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)
  const isUpdatingRef = useRef(false)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Local state for inputs (to prevent focus loss)
  const [timeout, setTimeoutValue] = useState(
    data.timeout !== undefined ? String(data.timeout) : '4',
  )
  const [repeatTimes, setRepeatTimes] = useState(
    data.repeatTimes !== undefined ? String(data.repeatTimes) : '0',
  )

  // Sync local state from external data (only when not updating)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setTimeoutValue(data.timeout !== undefined ? String(data.timeout) : '4')
      setRepeatTimes(
        data.repeatTimes !== undefined ? String(data.repeatTimes) : '0',
      )
    }
  }, [data.timeout, data.repeatTimes])

  const currentSoundClipId = data.soundClipId || ''
  const currentAllowExtensionDialing = data.allowExtensionDialing ?? false

  // Filter out empty or invalid options - only show options with actual data
  // Similar to Interactive Webhook, we don't want to display default/empty options
  const rawOptions = data.options || []
  const currentOptions = rawOptions.filter((option) => {
    // Only include options that have a meaningful digit value
    // Filter out empty strings, whitespace-only values, and null/undefined
    // A digit is required - action alone is not enough
    if (!option) return false
    const hasDigit = option.digit && option.digit.trim() !== ''
    return hasDigit
  })

  // Local state for digit inputs (to prevent focus loss)
  // Initialize with filtered options only (only those with digits)
  const [digitInputs, setDigitInputs] = useState<Record<number, string>>(() => {
    const digits: Record<number, string> = {}
    // Use the same filtering logic as currentOptions
    const rawOptions = data.options || []
    const validOptions = rawOptions.filter((option) => {
      if (!option) return false
      const hasDigit = option.digit && option.digit.trim() !== ''
      return hasDigit
    })
    validOptions.forEach((option, index) => {
      digits[index] = option.digit || ''
    })
    return digits
  })

  // Sync local digit state from external data (only when not updating)
  // Use a ref to track previous options signature to avoid infinite loops
  const prevOptionsSignatureRef = useRef<string>('')

  useEffect(() => {
    // Skip if we're in the middle of an update
    if (isUpdatingRef.current) {
      return
    }

    // Use filtered options (same logic as currentOptions)
    // Only include options with actual digit values
    const rawOptions = data.options || []
    const filteredOptions = rawOptions.filter((option) => {
      if (!option) return false
      const hasDigit = option.digit && option.digit.trim() !== ''
      return hasDigit
    })

    // Create a signature based on the digit values to compare
    const currentSignature = JSON.stringify(
      filteredOptions.map((opt) => opt.digit || ''),
    )

    // Only update if the signature actually changed
    if (prevOptionsSignatureRef.current !== currentSignature) {
      const digits: Record<number, string> = {}
      filteredOptions.forEach((option, index) => {
        digits[index] = option.digit || ''
      })

      // Update the signature ref immediately to prevent re-running
      prevOptionsSignatureRef.current = currentSignature

      // Use functional update to check if values actually changed
      setDigitInputs((prev) => {
        // Compare the actual values, not just the signature
        const prevValues = Object.keys(prev)
          .sort()
          .map((key) => prev[Number(key)] || '')
        const newValues = Object.keys(digits)
          .sort()
          .map((key) => digits[Number(key)] || '')

        // Only update if values are actually different
        if (JSON.stringify(prevValues) !== JSON.stringify(newValues)) {
          return digits
        }

        // Values are the same, don't update state
        return prev
      })
    }
  }, [data.options])

  const handleSoundClipChange = useCallback(
    (soundClipId: string | undefined, soundClipUuid: string | undefined) => {
      updateNodeData(
        id,
        withSoundClipSelection(dataRef.current, soundClipId, soundClipUuid),
        { replace: true },
      )
    },
    [id, updateNodeData],
  )

  const handleTimeoutChange = useCallback((value: string) => {
    isUpdatingRef.current = true
    setTimeoutValue(value)
  }, [])

  const handleTimeoutBlur = useCallback(() => {
    const timeoutValue = timeout ? parseInt(timeout, 10) : undefined
    // Clamp to valid range: 2-20
    const clampedValue =
      timeoutValue && !isNaN(timeoutValue)
        ? Math.max(2, Math.min(20, timeoutValue))
        : undefined
    updateNodeData(id, {
      ...dataRef.current,
      timeout: clampedValue || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, timeout])

  const handleRepeatTimesChange = useCallback((value: string) => {
    isUpdatingRef.current = true
    setRepeatTimes(value)
  }, [])

  const handleRepeatTimesBlur = useCallback(() => {
    const repeatValue = repeatTimes ? parseInt(repeatTimes, 10) : undefined
    // Clamp to valid range: 0-5
    const clampedValue =
      repeatValue !== undefined && !isNaN(repeatValue)
        ? Math.max(0, Math.min(5, repeatValue))
        : undefined
    updateNodeData(id, {
      ...dataRef.current,
      // Preserve explicit 0; only omit when truly undefined.
      repeatTimes: clampedValue ?? undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, repeatTimes])

  const handleAllowExtensionDialingChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, {
        ...dataRef.current,
        allowExtensionDialing: checked,
      })
    },
    [id, updateNodeData],
  )

  const handleDigitChange = useCallback((index: number, value: string) => {
    isUpdatingRef.current = true
    setDigitInputs((prev) => ({
      ...prev,
      [index]: value,
    }))
  }, [])

  const handleDigitBlur = useCallback(
    (index: number) => {
      const digitValue = digitInputs[index] || ''
      // Parse and clamp digit value to valid range: 0-9999
      const numValue = digitValue ? parseInt(digitValue, 10) : undefined
      const clampedValue =
        numValue !== undefined && !isNaN(numValue)
          ? Math.max(0, Math.min(9999, numValue))
          : undefined
      // Convert clamped value to string (matching legacy behavior)
      const clampedDigitValue =
        clampedValue !== undefined ? String(clampedValue) : digitValue
      const updatedOptions = [...currentOptions]
      updatedOptions[index] = {
        ...updatedOptions[index],
        digit: clampedDigitValue,
      }
      isUpdatingRef.current = true
      updateNodeData(id, {
        ...dataRef.current,
        options: updatedOptions,
      })
      // Update the signature ref immediately so the effect knows about the change
      prevOptionsSignatureRef.current = JSON.stringify(
        updatedOptions.map((opt) => opt.digit || ''),
      )
      isUpdatingRef.current = false
    },
    [id, updateNodeData, currentOptions, digitInputs],
  )

  const handleOptionsChange = useCallback(
    (options: AutoAttendantOption[]) => {
      isUpdatingRef.current = true

      // Filter options to match currentOptions logic
      const filteredOptions = options.filter((option) => {
        if (!option) return false
        const hasDigit = option.digit && option.digit.trim() !== ''
        return hasDigit
      })

      // Update digitInputs immediately with new options
      const digits: Record<number, string> = {}
      filteredOptions.forEach((option, index) => {
        digits[index] = option.digit || ''
      })
      setDigitInputs(digits)

      updateNodeData(id, {
        ...dataRef.current,
        options,
      })
      // Update the signature ref immediately so the effect knows about the change
      prevOptionsSignatureRef.current = JSON.stringify(
        filteredOptions.map((opt) => opt.digit || ''),
      )
      isUpdatingRef.current = false
    },
    [id, updateNodeData],
  )

  return {
    currentSoundClipId,
    timeout,
    repeatTimes,
    currentAllowExtensionDialing,
    currentOptions,
    digitInputs,
    handleSoundClipChange,
    handleTimeoutChange,
    handleTimeoutBlur,
    handleRepeatTimesChange,
    handleRepeatTimesBlur,
    handleAllowExtensionDialingChange,
    handleDigitChange,
    handleDigitBlur,
    handleOptionsChange,
    hasData:
      !!currentSoundClipId ||
      !!timeout ||
      !!repeatTimes ||
      currentOptions.length > 0,
  }
}
